/**
 * Zone 2 of the sidebar: the organisation's festivals and venues, each with an arrow that folds
 * out the event's own six pages (NAVIGATION.md).
 *
 * ---------------------------------------------------------------------------------------------
 * IT USED TO SAY "INGEN FESTIVALER ENNÅ" TO EVERYBODY, AND THAT WAS NOT A FACT ABOUT THE
 * ORGANISATION.
 *
 * Phase 0 rendered the empty state and made no API call, because there was no festivals endpoint
 * to call (§16 C-24) and a request that cannot succeed is worse than an honest empty list. The
 * seed has written two festivals the whole time; nothing served them. `GET /v1/festivals` exists
 * now (fase 2, steg 2 — decision 0010), so this is a query, and the empty state has gone back to
 * meaning what it says: this organisation has none yet.
 *
 * IT ASKS FOR NEITHER KIND, WHICH IS ONE REQUEST FOR A MENU. `listFestivalsQuerySchema.kind` is
 * optional and absent means both — the contract's own comment says the sidebar takes one answer,
 * which is one request rather than two.
 *
 * IT NO LONGER GROUPS THAT ANSWER BY KIND, AND THAT IS NAVIGATION.md OVERRULING THIS FILE. Until
 * that document landed, this section drew a "Festivaler" heading and a "Venues" heading and kept
 * the server's `(sort_order, name)` order inside each. NAVIGATION.md specifies **one flat list**
 * ("Flat liste over festivaler og venues"), and it specifies the order: "Alfabetisk innenfor to
 * grupper: aktive øverst, arkiverte nederst." Those two groups are ACTIVE and ARCHIVED, not
 * festival and venue — keeping the kind headings as well would have made four groups out of a
 * document that asks for one list. The manual `sortOrder` the server sorts by is still asked for
 * and still ignored here, because alphabetical is what the reader was promised.
 *
 * THE SECOND GROUP IS NOT BUILT, BECAUSE THERE IS NO FIELD FOR IT. `festivalDtoSchema` has no
 * archived flag and no status — an event is never described as archived anywhere in the contract
 * — so there is nothing to put at the bottom and nothing for a "Vis arkiverte" control to reveal.
 * Inventing a column to satisfy a sentence would be worse than waiting for it. When the field
 * arrives, the change is here: partition before sorting, and the control appears with the second
 * group.
 *
 * THE ICON CARRIES THE KIND. `festivals.kind` is one column with two values (decision 0010), so
 * the distinction is entirely a rendering job — and it may not be colour (DESIGN.md §3). The two
 * glyphs are in `festival-kind.ts`, with the labels, so the sidebar and the festival page cannot
 * disagree about which is which. The flat list makes that glyph the ONLY thing separating a
 * festival from a venue on screen, so `EventNavItem` puts the word itself in the accessible tree
 * where the headings used to say it.
 *
 * FOUR STATES (DESIGN.md §8), AND A FIFTH THAT IS NOT A STATE. Loading is three skeleton rows at
 * the real row height; error is the one `ErrorState` with a retry; empty says what belongs here
 * and offers the way to get it here; content is the list. The fifth is a reader without
 * `festival:list`, and that renders NOTHING — not the heading, not the empty state. A section
 * that says "ingen festivaler ennå" to somebody who is merely not allowed to see them is the
 * same lie this file was built to stop telling, and `Sidebar` already drops a static section
 * with nothing left in it for the same reason.
 *
 * WHY THE CREATE CONTROL LIVES HERE. `festival:create` is superadmin and admin only, and an
 * organisation with no festivals has NO other surface: no list page, no detail page, nothing but
 * this section and an empty state. So the empty state carries the primary button — "the way out
 * of the empty state is in it", the same arrangement Settings → Avdelinger uses. But a control
 * that only exists while the list is empty is a control you cannot find the second time, so once
 * there is at least one festival the same action moves to a `+` beside the section heading,
 * where it stays reachable from every page in the product. One component, one permission, two
 * places in the tree and never both at once — exactly `DepartmentsSettings`' `createButton`.
 *
 * WHAT IS STILL NOT BUILT, AND IS ITS OWN DELIVERY. NAVIGATION.md also asks for a sidebar that
 * collapses to icon width (events as initials with a tooltip, state remembered). That is not
 * here: it is a change to the frame rather than to this section, and doing it from inside the
 * festival list would have meant a second, private notion of how wide the sidebar is. (The
 * hamburger below 900px is built, in `AppShell` — QA6 K2 funn 4.)
 * ---------------------------------------------------------------------------------------------
 */
import { DEFAULT_COUNTRY_CODE, DEFAULT_TIMEZONE } from '@tvitvi/shared';
import type { Actor, FestivalDto, MeResponse } from '@tvitvi/shared';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { festivalsQueryOptions } from '@/api/queries/festivals';
import type { NavSectionDef } from '@/app/navigation';
import { actorFromMe, allows } from '@/auth/actor';
import { EmptyState } from '@/components/state/EmptyState';
import { ErrorState } from '@/components/state/ErrorState';
import { Skeleton } from '@/components/state/Skeleton';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { FestivalFormModal } from '@/features/festivals/FestivalFormModal';

import { eventLocationOf } from './event-nav';
import { EventNavItem } from './EventNavItem';
import { useExpandedEvents } from './expanded-events';
import { NAV_SECTION_HEADING } from './nav-styles';

/** Rows drawn while the answer is in flight. Three, because the seed makes two and one more. */
const SKELETON_ROWS = 3;

/**
 * `me` arrives as a prop rather than through `useMe()`, and that is not a style preference:
 * `useMe` is a `useSuspenseQuery`, so calling it here would make this component suspend in any
 * tree that has not already loaded `/v1/me` — and `Sidebar` is handed `me` explicitly precisely
 * so the menu never has to. One source, passed down, the way `Sidebar` already does it.
 */
export function FestivalNavSection({
  section,
  me,
}: {
  section: NavSectionDef;
  me: MeResponse;
}): ReactNode {
  const actor = actorFromMe(me);
  const organizationId = me.activeOrganizationId;
  /*
   * Not rendered-then-refused: the component that owns the query is never mounted, so nothing is
   * asked for and nothing claims the organisation is empty. `festival:list` is `yes` for four of
   * the five roles and `grant` for `external`, whose grants the browser does not have — the
   * server still decides, and the browser being narrower here costs an external a menu entry
   * rather than access to anything (see `auth/actor.ts`).
   */
  if (organizationId === null || !allows(actor, 'festival:list')) return null;

  return (
    <FestivalNavList
      section={section}
      organizationId={organizationId}
      actor={actor}
      canCreate={allows(actor, 'festival:create')}
    />
  );
}

interface FestivalNavListProps {
  section: NavSectionDef;
  organizationId: string;
  actor: Actor | null;
  canCreate: boolean;
}

function FestivalNavList({
  section,
  organizationId,
  actor,
  canCreate,
}: FestivalNavListProps): ReactNode {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const query = useQuery(festivalsQueryOptions(organizationId));
  const [createOpen, setCreateOpen] = useState(false);
  const { isExpanded, toggle, expand } = useExpandedEvents();

  /*
   * One subscription for the whole list rather than one per row: every row asks the same two
   * questions of the same address ("is this me" and "which year"), and a menu of ten events
   * should not hold ten subscriptions to answer them.
   */
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const here = eventLocationOf(pathname);

  /*
   * BARE FESTIVALENE (SPILLESTED-KONSERT, agent A). Spillestedene har fått sin egen seksjon,
   * «Spillested», med sin egen undermeny (`VenueNavSection`).
   *
   * Seksjonen finnes når organisasjonen har modulen Festival (0483) — `Sidebar` avgjør det. Før
   * modulene ble valgt skjulte den seg selv i et hus med bare spillesteder eller bare konserter
   * (sak 222.39); backfillen ga de husene Festival av, så menyen er den samme.
   */
  const all: readonly FestivalDto[] = query.data?.items ?? [];
  const items = all.filter((festival) => festival.kind === 'festival');
  /*
   * Alphabetical in the READER's language, not in code-point order: `Ørsta` sorts after `Å`
   * nowhere a Norwegian would look for it, and `localeCompare` with the active locale is the one
   * line that gets Æ, Ø and Å right in nb and diacritics right in en.
   */
  const sorted = [...items].sort((a, b) => a.name.localeCompare(b.name, i18n.language));

  return (
    /*
      `relative` is load-bearing and invisible: the rows below carry `sr-only` spans, which are
      `position: absolute`, and without a positioned ancestor their containing block is the
      document — which is how `<main>` acquired 954px of phantom scroll once already. See
      `AppShell` and `no-document-scroll.spec.ts`.
    */
    /*
      Inne i `<nav>` sin rulleflate (Atle, 29.09), så lista har ikke lenger sin egen `max-h` og
      sitt eget rullefelt: en lang festivalliste ruller med resten av menyen.
    */
    <div className="relative pt-3 first:pt-0" data-testid={`nav-section-${section.id}`}>
      <div className="flex min-h-7 items-center justify-between gap-1 px-2">
        {section.titleKey ? <h2 className={NAV_SECTION_HEADING}>{t(section.titleKey)}</h2> : null}
        {/* The second home of the create action — see the header. It appears only once the empty
            state (which carries the first) is gone, so there is never a pair of them. */}
        {canCreate && items.length > 0 ? (
          <IconButton
            icon="plus"
            label={t('festivals.action.create')}
            data-testid="festival-create"
            onClick={() => setCreateOpen(true)}
          />
        ) : null}
      </div>

      {query.isPending ? (
        <div className="flex flex-col gap-px pt-1" data-testid="festival-nav-skeleton">
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <Skeleton key={index} className="h-[var(--row-height-compact)] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <ErrorState
          inline
          error={query.error}
          onRetry={() => void query.refetch()}
          className="mt-1"
        />
      ) : items.length === 0 ? (
        <EmptyState
          compact
          icon="festival"
          title={t('nav.festivals.empty.title')}
          description={t('nav.festivals.empty.description')}
          {...(canCreate
            ? {
                action: (
                  <Button
                    variant="primary"
                    size="sm"
                    data-testid="festival-create-empty"
                    onClick={() => setCreateOpen(true)}
                  >
                    {t('festivals.action.create')}
                  </Button>
                ),
              }
            : {})}
        />
      ) : (
        <ul className="flex flex-col gap-px">
          {sorted.map((festival) => (
            <EventNavItem
              key={festival.id}
              festival={festival}
              organizationId={organizationId}
              actor={actor}
              expanded={isExpanded(festival.id)}
              onToggle={() => toggle(festival.id)}
              onOpen={() => expand(festival.id)}
              within={here?.festivalId === festival.id}
              currentEditionId={here?.festivalId === festival.id ? here.editionId : null}
            />
          ))}
        </ul>
      )}

      {canCreate ? (
        <FestivalFormModal
          open={createOpen}
          onOpenChange={setCreateOpen}
          organizationId={organizationId}
          defaultTimezone={DEFAULT_TIMEZONE}
          defaultCountryCode={DEFAULT_COUNTRY_CODE}
          // Making the first festival and then being left looking at the same empty sidebar is
          // the worst possible answer to "it worked". You land on the thing you made — and on its
          // first årgang, which is step two of «Ny festival» (sak 330.1).
          onSaved={(festival) => {
            void navigate({
              to: '/festivals/$festivalId',
              params: { festivalId: festival.id },
              search: { firstEdition: true },
            });
          }}
        />
      ) : null}
    </div>
  );
}
